/** Minimal .ics generator for a daily recurring practice reminder. We have
    no backend and no reliable web push scheduling, so a calendar invite is
    the one honest commitment device available — it actually works, and it
    doesn't pretend to be a notification system we can't deliver. */

export type IcsOptions = {
  title: string;
  description?: string;
  /** 24h "HH:MM" local time the event starts */
  time: string;
  durationMinutes?: number;
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateTimeStamp(date: Date, hh: number, mm: number): string {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(hh)}${pad(mm)}00`;
}

export function buildIcs({ title, description, time, durationMinutes = 5 }: IcsOptions): string {
  const [hhStr, mmStr] = time.split(":");
  const hh = Number(hhStr) || 8;
  const mm = Number(mmStr) || 0;
  const start = new Date();
  const dtStart = localDateTimeStamp(start, hh, mm);
  const end = new Date(start);
  end.setHours(hh, mm + durationMinutes);
  const dtEnd = localDateTimeStamp(end, end.getHours(), end.getMinutes());
  const now = new Date();
  const dtStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}Z`;
  const uid = `diction-dojo-${Date.now()}@local`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Diction Dojo//Practice Reminder//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    "RRULE:FREQ=DAILY",
    `SUMMARY:${escapeIcs(title)}`,
    description ? `DESCRIPTION:${escapeIcs(description)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter((l): l is string => l !== null);

  return lines.join("\r\n");
}

function escapeIcs(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

export function downloadIcs(options: IcsOptions, filename = "diction-dojo-practice.ics"): void {
  const blob = new Blob([buildIcs(options)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
